import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Programs from "./pages/Programs";
import Scholars from "./pages/Scholars";
import PartnerLogin from "./pages/PartnerLogin";
import PartnerRegister from "./pages/PartnerRegister";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerOpportunities from "./pages/PartnerOpportunities";
import PartnerApplications from "./pages/PartnerApplications";
import PartnerAnalytics from "./pages/PartnerAnalytics";
import PartnerMessages from "./pages/PartnerMessages";
import PartnerOrganization from "./pages/PartnerOrganization";
import PartnerTeam from "./pages/PartnerTeam";
import PartnerSettings from "./pages/PartnerSettings";
import PartnerIntegrations from "./pages/PartnerIntegrations";
import PartnerPlaceholder from "./pages/PartnerPlaceholder";
import PartnerAddProgram from "./pages/PartnerAddProgram";
import PartnerViewProgram from "./pages/PartnerViewProgram";
import PartnerPosts from "./pages/PartnerPosts";
import PartnerWorkspaceShell from "./components/partner/PartnerWorkspaceShell";
import { PartnerBillingView, PartnerManageCampaignView, PartnerUpgradeView } from "./components/partner/PartnerViews";
import {
  PartnerLegacyDashboardRedirect,
  PartnerLegacyOpportunityRedirect,
  PartnerLegacyProgramRedirect,
} from "./pages/PartnerLegacyRedirects";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import AuthRecovery from "./pages/AuthRecovery";
import GuardianConsent from "./pages/GuardianConsent";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PartnerCalendarCallback from "./pages/PartnerCalendarCallback";
import InviteLanding from "./pages/InviteLanding";
import StoryLanding from "./pages/StoryLanding";

const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/programs" element={<Programs />} />
      <Route path="/scholars" element={<Scholars />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/guardian/consent" element={<GuardianConsent />} />
      <Route path="/invite/:code" element={<InviteLanding />} />
      <Route path="/student-story/:storyId" element={<StoryLanding />} />

      {/* Partner Portal: public */}
      <Route path="/partners/login" element={<PartnerLogin />} />
      <Route path="/partners/register" element={<PartnerRegister />} />
      <Route path="/partners/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/recovery" element={<AuthRecovery />} />
      <Route path="/auth/reset-password" element={<AuthRecovery />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/partners/calendar/callback" element={<PartnerCalendarCallback />} />

      {/* Partner Portal: authenticated workspace (shared data cache) */}
      <Route element={<PartnerWorkspaceShell />}>
        <Route path="/partners/dashboard" element={<PartnerDashboard />} />
        <Route path="/partners/programs" element={<PartnerOpportunities />} />
        <Route path="/partners/programs/new" element={<PartnerAddProgram />} />
        <Route path="/partners/programs/edit/:id" element={<PartnerAddProgram />} />
        <Route path="/partners/programs/view/:id" element={<PartnerViewProgram />} />
        {/* Legacy opportunity URLs */}
        <Route path="/partners/opportunities" element={<PartnerLegacyOpportunityRedirect />} />
        <Route path="/partners/opportunities/new" element={<PartnerLegacyProgramRedirect />} />
        <Route path="/partners/opportunities/edit/:id" element={<PartnerLegacyOpportunityRedirect />} />
        <Route path="/partners/opportunities/view/:id" element={<PartnerLegacyOpportunityRedirect />} />
        <Route path="/partners/opportunities/:id" element={<PartnerLegacyOpportunityRedirect />} />
        <Route path="/partners/applications" element={<PartnerApplications />} />
        <Route path="/partners/messages" element={<PartnerMessages />} />
        <Route path="/partners/posts" element={<PartnerPosts />} />
        <Route path="/partners/analytics" element={<PartnerAnalytics />} />
        <Route path="/partners/organization" element={<PartnerOrganization />} />
        <Route path="/partners/team" element={<PartnerTeam />} />
        <Route path="/partners/settings" element={<PartnerSettings />} />
        <Route path="/partners/integrations" element={<PartnerIntegrations />} />

        <Route
          path="/partners/community"
          element={
            <PartnerPlaceholder
              title="Community"
              description="Discussion spaces and peer engagement for your organization's learners."
            />
          }
        />
        <Route
          path="/partners/events"
          element={
            <PartnerPlaceholder
              title="Events"
              description="Host and promote events that bring learners together."
            />
          }
        />
        <Route
          path="/partners/reports"
          element={
            <PartnerPlaceholder
              title="Reports"
              description="Exportable reports for stakeholders and leadership."
            />
          }
        />
        <Route
          path="/partners/media"
          element={
            <PartnerPlaceholder
              title="Media Library"
              description="Centralized assets for opportunities and organization profiles."
            />
          }
        />
        <Route
          path="/partners/billing"
          element={<PartnerBillingView />}
        />
        <Route
          path="/partners/manage-campaign"
          element={<PartnerManageCampaignView />}
        />
        <Route
          path="/partners/upgrade"
          element={<PartnerUpgradeView />}
        />
        <Route
          path="/partners/notifications"
          element={
            <PartnerPlaceholder
              title="Notifications"
              description="Application updates, deadlines, and activity alerts."
            />
          }
        />
        <Route
          path="/partners/help"
          element={
            <PartnerPlaceholder
              title="Help Center"
              description="Guides, documentation, and partner success resources."
            />
          }
        />

        <Route path="/partners/dashboard/program/new" element={<PartnerLegacyProgramRedirect />} />
        <Route path="/partners/dashboard/legacy" element={<PartnerLegacyDashboardRedirect />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <Toaster position="top-right" richColors />
  </BrowserRouter>
);

export default App;
